import { type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import { LineItemType } from "legacyComponents/LineItemContainer.types";
import moment from "moment";

import type { InvoiceFormat } from "@btnet/services-btnet/handlers/models/invoiceFormat";

import { type BTSelectItem } from "types/apiResponse/apiResponse";
import { BTLocalStorage, type ILocalStorage } from "types/btStorage";
import { DateRangeFilterOptionItem } from "types/enum";

import { add, divide, multiply, subtract } from "utilities/math/math";
import { isNullOrUndefined } from "utilities/object/object";

import { type IDateRangeFormValues } from "commonComponents/entity/filters/DateRange/DateRange";
import {
    type ILineItemEntityDetails,
    type ILineItemPresentationalDetails,
    type ILineItemsToInvoiceFormValues,
    LineItemAncestorEntity,
    type LineItemForInvoice,
    type LineItemsToInvoiceEntity,
    TableDataType,
} from "commonComponents/entity/invoicing/LineItemsToInvoice/LineItemsToInvoice.api.types";
import { type RelatedItemType } from "commonComponents/entity/relatedItem/RelatedItem.types";
import { type TaxGroupServiceListItemExtraData } from "commonComponents/financial/TaxRateSelect/TaxRateSelect.api.types";

import { mapRelatedEntityLineItemsToInvoiceLineItems } from "entity/ownerInvoice/OwnerInvoice/OwnerInvoice.utility";
import {
    type IOwnerInvoiceLineItem,
    type OwnerInvoiceLineItem,
} from "entity/ownerInvoice/OwnerInvoiceLineItemContainer/OwnerInvoiceLineItemContainer.types";

export const getEntityTypeConfig = (
    entityType?: LineItemType,
    accountingIntegrationName?: string,
): {
    text: string;
    iconName: "Accounting" | "Bills" | "TimeClock" | undefined;
    dateLabel: string;
} => {
    const accountingIntegrationNameForDisplay = accountingIntegrationName || "Accounting";
    switch (entityType) {
        case LineItemType.OtherAccountingCost:
            return {
                text: `${accountingIntegrationNameForDisplay} Cost`,
                iconName: "Accounting",
                dateLabel: "Invoice date",
            };
        case LineItemType.Bill:
            return {
                text: "Bill",
                iconName: "Bills",
                dateLabel: "Invoice date",
            };
        case LineItemType.TimeCardItem:
            return {
                text: "Time Clock",
                iconName: "TimeClock",
                dateLabel: "Shift date",
            };
        default:
            return {
                text: "Entity",
                iconName: undefined,
                dateLabel: "Invoice date",
            };
    }
};

export const isLineItemRow = (
    item: LineItemForInvoice | LineItemAncestorEntity,
): item is LineItemForInvoice => {
    return item.type === TableDataType.LineItem;
};

export const isParentRow = (
    item: LineItemForInvoice | LineItemAncestorEntity,
): item is LineItemAncestorEntity => {
    return item.type === TableDataType.Parent;
};

export const isGrandparentRow = (
    item: LineItemForInvoice | LineItemAncestorEntity,
): item is LineItemAncestorEntity => {
    return item.type === TableDataType.GrandParent;
};

export const isAncestorRow = (
    item: LineItemForInvoice | LineItemAncestorEntity,
): item is LineItemAncestorEntity => {
    return item.type === TableDataType.Parent || item.type === TableDataType.GrandParent;
};

export const BLOCKED_BY_SELECTION_INVOICES_MESSAGE =
    "This allowance has been invoiced through its selection options and cannot be invoiced from the estimate";

export const ALL_BLOCKED_BY_SELECTION_INVOICES_MESSAGE =
    "These items cannot be invoiced because their selection options already have invoices";

export const isFullyInvoiced = (lineItem: LineItemForInvoice) => {
    return (
        lineItem.amountRemaining === 0 ||
        (lineItem.amountRemaining < 0 && lineItem.amountInvoiced > 0) ||
        (lineItem.amountRemaining > 0 && lineItem.amountInvoiced < 0)
    );
};

export const isInvoiceable = (lineItem: LineItemForInvoice): boolean => {
    return !isFullyInvoiced(lineItem) && !lineItem.isBlockedBySelectionInvoices;
};

export const calculateLineItemAmount = (
    lineItem: LineItemForInvoice,
    percent: number | undefined,
) => {
    return percent !== undefined
        ? (lineItem.builderCost * percent) / 100 + lineItem.builderCost
        : 0;
};

/**
 * The invoice amount for a line marked up by `markupPerUnit` on each unit.
 *
 * The billed rate is unitCost + markupPerUnit, so the amount is that rate times the quantity. It is
 * not a percentage of cost, so it stays correct when the shift has no configured labor cost.
 *
 * A cleared field means no markup has been entered, not that the line bills nothing — the cost does
 * not stop existing mid-keystroke. Treating it as zero markup bills the line at cost, which is where
 * the field settles on blur, so the amount does not dip and recover while the builder is typing.
 */
export const calculateAmountFromMarkupPerUnit = (
    lineItem: LineItemForInvoice,
    markupPerUnit: number | undefined,
) => {
    const unitPrice = add(lineItem.unitCost, markupPerUnit ?? 0);
    return multiply(unitPrice, lineItem.quantity).toNumber();
};

/**
 * The markup percentage equivalent to `markupPerUnit`, for the fields that still carry a percentage
 * downstream. Returns 0 when there is no cost to measure against — a percentage of zero cost is
 * undefined, and the per-unit markup is what drives the amount in that case.
 */
export const calculatePercentFromMarkupPerUnit = (
    lineItem: LineItemForInvoice,
    markupPerUnit: number | undefined,
) => {
    if (markupPerUnit === undefined || lineItem.builderCost === 0) {
        return 0;
    }
    const amount = calculateAmountFromMarkupPerUnit(lineItem, markupPerUnit);
    return multiply(
        divide(subtract(amount, lineItem.builderCost), lineItem.builderCost),
        100,
    ).toNumber();
};

/**
 * Groups time clock line items by cost code and creates virtual parent entities for each group.
 * Updates the group property on each time clock line item to point to its cost code group.
 *
 * @param lineItems - Array of line items to process
 * @returns Array of virtual parent entities representing cost code groups
 */
export const createTimeClockCostCodeGroups = (lineItems: LineItemForInvoice[]) => {
    const costCodeGroups = new Map<number, LineItemForInvoice[]>();

    // Group time clock items by cost code
    lineItems.forEach((item) => {
        if (item.lineItemType === LineItemType.TimeCardItem) {
            if (!costCodeGroups.has(item.costCode)) {
                costCodeGroups.set(item.costCode, []);
            }
            costCodeGroups.get(item.costCode)!.push(item);
        }
    });

    // Create virtual ancestor groups by cost code
    Array.from(costCodeGroups.entries()).forEach(([costCode, items]) => {
        const firstItem = items[0];
        const totalAmount = items.reduce((sum, item) => sum + item.builderCost, 0);

        const group = new LineItemAncestorEntity(
            {
                id: `costCode-${costCode}`,
                title: "Labor", // Cost type -- but it is always labor
                totalAmount: totalAmount,
                parentId: null,
                subTitle: firstItem.costCodeTitle,
                entityType: LineItemType.TimeCardItem,
                dateToFilter: null, // Cost code groups don't have a single date
                attachedFiles: null,
                userName: null,
                hasOvertime: items.some((item) => item.parentEntity?.hasOvertime),
            },
            TableDataType.Parent,
        );

        items.forEach((item) => {
            item.group = group;
        });
    });
};

const getUniqueValuesById = (ancestors: LineItemAncestorEntity[]): LineItemAncestorEntity[] => {
    return ancestors.filter((g, index, arr) => {
        return arr.map((mapObj) => mapObj.id).indexOf(g.id) === index;
    });
};

export const getUniqueGroups = (lineItems: LineItemForInvoice[]): LineItemAncestorEntity[] => {
    const groups = lineItems
        .map((li) => li.group)
        .filter((g): g is LineItemAncestorEntity => g !== undefined);
    return getUniqueValuesById(groups);
};

export const getUniqueParents = (lineItems: LineItemForInvoice[]): LineItemAncestorEntity[] => {
    const parents = lineItems
        .map((li) => li.parentEntity)
        .filter((g): g is LineItemAncestorEntity => g !== undefined);
    return getUniqueValuesById(parents);
};

export const getUniqueGrandparents = (
    lineItems: LineItemForInvoice[],
): LineItemAncestorEntity[] => {
    const grandparents = lineItems
        .map((li) => li.grandparentEntity)
        .filter((g): g is LineItemAncestorEntity => g !== undefined);
    return getUniqueValuesById(grandparents);
};

/**
 * Maps line item entity API responses to presentational details for the UI.
 * Combines multiple entity types into a single presentational model with proper sorting.
 *
 * @param data - Array of entity responses from the API
 * @param entityTypes - Corresponding entity types for each response
 * @returns Unified presentational details for rendering the invoice modal
 */
export const mapLineItemToInvoiceEntityToPresentationalDetails = (
    data: LineItemsToInvoiceEntity[],
    entityTypes: LineItemType[],
) => {
    const dataMap = new Map<LineItemType, ILineItemEntityDetails>();
    for (let i = 0; i < data.length; i++) {
        const entityTypeValue = entityTypes[i];
        const entityDetails = {
            invalidTaxMethodsSelected: data[i].invalidTaxMethodsSelected,
            showLineItemsToOwner: data[i].showLineItemsToOwner,
            supportsAttachments: data[i].supportsAttachments,
            supportsLineItemStacking: data[i].supportsLineItemStacking,
            taxMethod: data[i].taxMethod,
            lineItems: [...data[i].lineItems],
            builderVarianceCodes: data[i].builderVarianceCodes,
            filterByVarianceCodes: data[i].filterByVarianceCodes,
        };
        dataMap.set(entityTypeValue, entityDetails);
    }
    let lineItems = data.flatMap((x) => x.lineItems);

    // Sort line items if we have multiple entity types and selected entities
    const hasMultipleEntityTypes = entityTypes.length > 1;
    if (hasMultipleEntityTypes) {
        lineItems = sortLineItemsByInitialSelection(lineItems);
    }

    const result: ILineItemPresentationalDetails = {
        dateRangeOptions: data.find((x) => x.dateRangeOptions)?.dateRangeOptions,
        hideDescriptionAndNotesCheckbox: data.every((x) => x.hideDescriptionAndNotesCheckbox),
        shouldDisplayGrandparentRows: data.every((x) => x.shouldDisplayGrandparentRows),
        shouldDisplayParentRows: data.every((x) => x.shouldDisplayParentRows),
        isCostPlusWorkflow: data.every((x) => x.isCostPlusWorkflow),
        isOpenBookJob: data.every((x) => x.isOpenBookJob),
        // Only Time Clock entities carry a bill-rate setting; consider just those so a non-timeclock
        // entity (which always reports false) doesn't suppress the popover.
        billRateEnabledForJob: data
            .filter((_x, i) => entityTypes[i] === LineItemType.TimeCardItem)
            .every((x) => x.billRateEnabledForJob),
        mappingWizardTitle: data.length === 1 ? data[0].mappingWizardTitle : undefined,
        pageSectionTitle: data.length === 1 ? data[0].pageSectionTitle : undefined,
        lineItems: lineItems,
        entityMap: dataMap,
        builderVarianceCodes: data.flatMap((x) => x.builderVarianceCodes),
        filterByVarianceCodes: data.some((x) => x.filterByVarianceCodes),
    };
    return result;
};

/**
 * A stacking-capable cost source has two rows sharing a cost code, so they auto-stack rather than
 * appending separately. Shared by the Costs modal's submit path and the auto-fill adapter.
 */
export const hasStackableLineItems = (
    lineItems: LineItemForInvoice[],
    entityMap: Map<LineItemType, ILineItemEntityDetails>,
): boolean =>
    lineItems.some(
        (lineItem) =>
            entityMap.get(lineItem.lineItemType)?.supportsLineItemStacking &&
            lineItems.filter((li) => li.costCode === lineItem.costCode).length > 1,
    );

// Literal type (not `keyof ILocalStorage`) so importers keep the precise BTLocalStorage.get/set value
// type — a wide key erases the per-key narrowing once the const crosses a module boundary.
export const addInvoiceLineItemPreferencesStorageKey =
    "bt-object-addInvoiceLineItemPreferences" as const satisfies keyof ILocalStorage;

/**
 * Merges the builder's saved per-type "include descriptions" choice (localStorage) onto a base map.
 * Shared by the Costs modal's load effect and the auto-fill adapter so both honor the same stored
 * preference; any missing or malformed storage leaves the base map untouched.
 */
export const applyStoredIncludeDescriptions = (
    base: Map<LineItemType, boolean>,
    globalUserId: number | null | undefined,
): Map<LineItemType, boolean> => {
    const merged = new Map(base);
    if (!globalUserId) {
        return merged;
    }
    try {
        const stored = BTLocalStorage.get(addInvoiceLineItemPreferencesStorageKey)?.[
            globalUserId.toString()
        ]?.includeDescriptions;
        Object.entries(stored ?? {}).forEach(([entityType, value]) => {
            // A key that is not a valid LineItemType parses to NaN and is ignored by merged.has().
            const key = parseInt(entityType) as LineItemType;
            if (merged.has(key) && typeof value === "boolean") {
                merged.set(key, value);
            }
        });
    } catch {
        // Leave the base defaults on any storage/parse error.
    }
    return merged;
};

export const getCostPlusCombinedFlowLineItemTypes = () => [
    LineItemType.Bill,
    LineItemType.TimeCardItem,
    LineItemType.OtherAccountingCost,
];

export const isCostPlusCombinedWorkflow = (lineItemType: LineItemType): boolean => {
    const costPlusWorkflowTypes = getCostPlusCombinedFlowLineItemTypes();
    return costPlusWorkflowTypes.includes(lineItemType);
};

/**
 * Sorts line items by initial selection status.
 * Items that are initially selected appear first.
 *
 * @param lineItems - Array of line items to sort
 * @returns Sorted array of line items with initially selected items first
 */
export const sortLineItemsByInitialSelection = (
    lineItems: LineItemForInvoice[],
): LineItemForInvoice[] => {
    const selected = lineItems.filter((item) => item.isSelected);
    const notSelected = lineItems.filter((item) => !item.isSelected);
    return [...selected, ...notSelected];
};

export const handleAddLineItemsToInvoice = async (
    values: ILineItemsToInvoiceFormValues,
    entityMap: Map<LineItemType, ILineItemEntityDetails>,
    existingLineItems: IOwnerInvoiceLineItem[],
    newLineItemId: number,
    taxGroup?: BTSelectItem<TaxGroupServiceListItemExtraData>,
    onAddInvoicedAttachments?: (
        lineItems: LineItemForInvoice[],
    ) => Promise<BTFileSystem[] | undefined>,
    effectiveTaxVersion?: number,
    invoiceFormat?: InvoiceFormat,
    isOptedOutOfTaxes?: boolean,
    hasBillableRatesForJob: boolean = false,
    showTimeCardTags: boolean = false,
    relatedEntityTagTypes: RelatedItemType[] = [],
) => {
    const updatedLineItems = [...existingLineItems];
    const newLineItems: OwnerInvoiceLineItem[] = [];
    const addedAttachments: BTFileSystem[] = [];

    // This is done for the estimates line item use case where it contains the original
    // line item type of the item before it was pushed to the estimate.
    const lineItemIdToEntityTypeMap = new Map<number, LineItemType>();
    for (const [entityType, entityDetails] of entityMap.entries()) {
        entityDetails.lineItems.forEach((lineItem) => {
            lineItemIdToEntityTypeMap.set(lineItem.lineItemId, entityType);
        });
    }

    const lineItemsByEntityType = values.lineItems.reduce(
        (groups: Map<LineItemType, LineItemForInvoice[]>, li) => {
            const liTypeGroup = lineItemIdToEntityTypeMap.get(li.lineItemId);
            if (!liTypeGroup) {
                // This should never happen, but prevent runtime errors if it does
                return groups;
            }

            const currentGroup = groups.get(liTypeGroup) ?? [];
            groups.set(liTypeGroup, [...currentGroup, li]);
            return groups;
        },
        new Map<LineItemType, LineItemForInvoice[]>(),
    );

    for (const [entityType, lineItems] of lineItemsByEntityType.entries()) {
        const entityDetails = entityMap.get(entityType);
        if (!entityDetails) {
            // This should never happen, but prevent runtime errors if it does
            continue;
        }

        if (
            entityDetails.supportsAttachments &&
            values.includeAttachments &&
            onAddInvoicedAttachments
        ) {
            const attachmentsToAddForCurrentEntityType = await onAddInvoicedAttachments(lineItems);
            if (attachmentsToAddForCurrentEntityType) {
                addedAttachments.push(...attachmentsToAddForCurrentEntityType);
            }
        }

        const includeDescriptions = values.includeDescriptions.get(entityType) ?? false;

        const [lineItemsFromRelatedEntity, newLineItemIdValue] =
            mapRelatedEntityLineItemsToInvoiceLineItems(
                lineItems,
                updatedLineItems,
                newLineItemId,
                includeDescriptions,
                entityDetails.supportsLineItemStacking,
                values.stackLineItems,
                taxGroup,
                effectiveTaxVersion,
                invoiceFormat,
                isOptedOutOfTaxes,
                hasBillableRatesForJob,
                showTimeCardTags,
                relatedEntityTagTypes,
            );
        newLineItems.push(...lineItemsFromRelatedEntity);
        newLineItemId = newLineItemIdValue;
    }

    const ownerInvoiceLineItems = [...updatedLineItems, ...newLineItems];

    return { ownerInvoiceLineItems, addedAttachments, nextLineItemId: newLineItemId };
};

export interface IInvoiceLineItemFilterOptions {
    hasMultipleEntityTypes: boolean;
    filterByVarianceCodes: boolean | undefined;
    builderVarianceCodes: number[] | undefined;
}

// dropdown values that only require a start date
const SINGLE_DATE_DROPDOWN_VALUES = [
    DateRangeFilterOptionItem.Weekly,
    DateRangeFilterOptionItem.BiWeekly,
    DateRangeFilterOptionItem.SemiMonthly,
    DateRangeFilterOptionItem.Monthly,
];
const REQUIRE_DATE_DROPDOWN_VALUES = [
    ...SINGLE_DATE_DROPDOWN_VALUES,
    DateRangeFilterOptionItem.CustomDates,
];

const shouldFilterByDateRange = (dateRangeValue: IDateRangeFormValues): boolean => {
    const dateDropdownSelectedValue = dateRangeValue.selectedItem;
    const hasStartDate = !isNullOrUndefined(dateRangeValue.dateRange?.[0]);
    const hasEndDate = !isNullOrUndefined(dateRangeValue.dateRange?.[1]);
    return (
        !REQUIRE_DATE_DROPDOWN_VALUES.includes(dateDropdownSelectedValue) || // selected dropdown value that doesn't require date. i.e. past 7 days
        (SINGLE_DATE_DROPDOWN_VALUES.includes(dateDropdownSelectedValue) && hasStartDate) ||
        (hasStartDate && hasEndDate)
    );
};

/**
 * Filters line items to those whose {@link LineItemForInvoice.dateToFilter} falls within the
 * selected date range. Bounds are inclusive and day-granular, so items dated on the range's
 * first or last day are kept.
 *
 * @param lineItems - Line items to filter
 * @param dateRangeValue - The active date-range filter selection
 * @param hasMultipleEntityTypes - When true, items without a date (e.g. consolidated costs) are kept
 */
export const filterLineItemsByDateRange = (
    lineItems: LineItemForInvoice[],
    dateRangeValue: IDateRangeFormValues,
    hasMultipleEntityTypes: boolean,
): LineItemForInvoice[] => {
    if (!shouldFilterByDateRange(dateRangeValue)) {
        return lineItems;
    }

    let startDate = dateRangeValue.dateRange?.[0] ?? moment();
    let endDate = moment();

    switch (dateRangeValue.selectedItem) {
        case DateRangeFilterOptionItem.CustomDates:
            endDate = dateRangeValue.dateRange?.[1] ?? endDate;
            break;
        case DateRangeFilterOptionItem.Weekly:
            endDate = moment(startDate).add(6, "d");
            break;
        case DateRangeFilterOptionItem.BiWeekly:
            endDate = moment(startDate).add(13, "d");
            break;
        case DateRangeFilterOptionItem.SemiMonthly:
            // Start is restricted to the 1st or 15th: first half runs through the 15th,
            // second half runs to the end of the month. Express both ends declaratively
            // (date(15) / endOf("M")) so the range is correct for any first-half start day,
            // not just the 1st. Use date() (day of month), not day() (day of week, always < 14).
            if (startDate.date() < 15) {
                endDate = moment(startDate).date(15);
            } else {
                endDate = moment(startDate).endOf("M");
            }
            break;
        case DateRangeFilterOptionItem.Monthly:
            startDate = moment(startDate).startOf("M");
            endDate = moment(startDate).endOf("M");
            break;
        case DateRangeFilterOptionItem.Today:
        case DateRangeFilterOptionItem.TodayAndYesterday:
        case DateRangeFilterOptionItem.Past7Days:
        case DateRangeFilterOptionItem.Past14Days:
        case DateRangeFilterOptionItem.Past30Days:
        case DateRangeFilterOptionItem.Past45Days:
        case DateRangeFilterOptionItem.Past60Days:
        case DateRangeFilterOptionItem.Past90Days:
        case DateRangeFilterOptionItem.Past180Days:
        case DateRangeFilterOptionItem.Past365Days:
            // the enum value correlates to number of days to subtract
            startDate = moment(endDate).add(dateRangeValue.selectedItem, "d");
            break;
        default:
            // all dates or invalid range selected, return all line items
            return lineItems;
    }

    // use startOf/endOf to get all shifts on days regardless of time
    startDate = moment(startDate).startOf("d");
    endDate = moment(endDate).endOf("d");

    return lineItems.filter((li) => {
        if (isNullOrUndefined(li.dateToFilter)) {
            // if no dateToFilter,
            // - if multiple entity types (launched from consolidated costs) show the line items
            // - otherwise, hide the line items (launched from single entity)
            return hasMultipleEntityTypes;
        }
        // day-granular inclusive bounds ("[]") so items dated on the range's
        // start or end day are kept; moment's default is exclusive on both ends
        return li.dateToFilter?.isBetween(startDate, endDate, "day", "[]");
    });
};

/**
 * Applies every "Add from costs" filter (builder variance, entity type, approved-bills-only, and
 * date range) to the full owner-invoiceable line item set. All filters compose independently and
 * order-independently, so toggling any one control preserves the others.
 *
 * @param allLineItems - The full, unfiltered line item set (`presentationalDetails.lineItems`)
 * @param formValues - Current form filter state
 * @param options - Presentational flags that gate the variance and entity-type filters
 */
export const applyInvoiceLineItemFilters = (
    allLineItems: LineItemForInvoice[],
    formValues: ILineItemsToInvoiceFormValues,
    options: IInvoiceLineItemFilterOptions,
): LineItemForInvoice[] => {
    // Start from the full owner-invoiceable set so every filter composes from the same baseline
    let visibleLineItems = allLineItems.filter((li) => li.ownerPrice !== 0);

    // Apply builder variance filter unless the user opted to include variance costs
    const { builderVarianceCodes } = options;
    if (options.filterByVarianceCodes && builderVarianceCodes && !formValues.includeVarianceCosts) {
        visibleLineItems = visibleLineItems.filter(
            (li) => !builderVarianceCodes.includes(li.varianceCode),
        );
    }

    // Apply entity type filter
    // Note that this will need to be updated if there are multiple line item types within the entity types you are filtering on (e.g. estimates)
    if (options.hasMultipleEntityTypes && formValues.selectedEntityTypes?.length > 0) {
        visibleLineItems = visibleLineItems.filter((li) =>
            formValues.selectedEntityTypes.includes(li.lineItemType),
        );
    }
    if (
        formValues.showApprovedBillsOnly &&
        formValues.selectedEntityTypes.includes(LineItemType.Bill)
    ) {
        visibleLineItems = visibleLineItems.filter(
            (li) => li.lineItemType !== LineItemType.Bill || li.isApproved,
        );
    }

    // Apply date range filter
    if (formValues.dateRange) {
        visibleLineItems = filterLineItemsByDateRange(
            visibleLineItems,
            formValues.dateRange,
            options.hasMultipleEntityTypes,
        );
    }

    return visibleLineItems;
};
